import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import "./TerminalPanel.css";

/**
 * TerminalPanel — VS Code-style integrated terminal at the bottom.
 * Simulates a live shell session with typed output on mount.
 * User can type commands for canned responses.
 */

const BOOT_SEQUENCE = [
  { delay: 0,    text: "",                                         class: "" },
  { delay: 100,  text: "tommy@portfolio:~/portfolio $ npm start",   class: "t-cmd" },
  { delay: 600,  text: "> portfolio@1.0.0 start",                  class: "t-dim" },
  { delay: 800,  text: "> react-scripts start",                    class: "t-dim" },
  { delay: 1200, text: "",                                         class: "" },
  { delay: 1400, text: "Starting the development server...",       class: "t-info" },
  { delay: 2000, text: "",                                         class: "" },
  { delay: 2200, text: "Compiled successfully!",                   class: "t-success" },
  { delay: 2400, text: "",                                         class: "" },
  { delay: 2600, text: "You can now view portfolio in the browser.", class: "t-dim" },
  { delay: 2800, text: "  Local:    http://localhost:3000",        class: "t-url" },
  { delay: 3000, text: "  Network:  http://192.168.1.12:3000",    class: "t-url" },
  { delay: 3200, text: "",                                         class: "" },
];

const CANNED = {
  help: [
    "Available commands: help, whoami, skills, contact, clear",
  ],
  whoami: [
    "tommy boubaker — fullstack developer",
    "based in Lavaur, France",
    "open to remote opportunities",
  ],
  skills: [
    "LANGUAGES  : JavaScript, TypeScript, Python",
    "FRONTEND   : React, Next.js, CSS",
    "BACKEND    : Node.js, Express, PostgreSQL, Redis",
    "DEVOPS     : Docker, GitHub Actions, AWS",
  ],
  contact: [
    "email   : tommy.boubaker@free.fr",
    "github  : github.com/tommy-BOU",
    "linkedin: linkedin.com/in/tommyboubaker-82b02381/",
  ],
  clear: "__CLEAR__",
};

function TerminalPanel({ onClose }) {
  const { locale } = useApp();
  const [lines, setLines] = useState([]);
  const [input, setInput] = useState("");
  const [ready, setReady] = useState(false);
  const [activeTab, setActiveTab] = useState("TERMINAL");
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Boot sequence on mount
  useEffect(() => {
    const timers = BOOT_SEQUENCE.map(({ delay, text, class: cls }) =>
      setTimeout(() => {
        setLines((prev) => [...prev, { text, cls }]);
        if (delay >= 3600) setReady(true);
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  const handleKey = (e) => {
    if (e.key !== "Enter") return;
    const cmd = input.trim().toLowerCase();
    setInput("");

    const promptLine = { text: `tommy@portfolio:~/portfolio $ ${input}`, cls: "t-cmd" };
    if (!cmd) { setLines((p) => [...p, promptLine]); return; }

    const resp = CANNED[cmd];
    if (resp === "__CLEAR__") { setLines([]); return; }

    if (resp) {
      setLines((p) => [...p, promptLine, ...resp.map((t) => ({ text: t, cls: "t-output" })), { text: "", cls: "" }]);
    } else {
      setLines((p) => [
        ...p,
        promptLine,
        { text: `command not found: ${cmd}. Type 'help' for available commands.`, cls: "t-error" },
        { text: "", cls: "" },
      ]);
    }
  };

  return (
    <div className="terminal-panel">
      {/* Panel header */}
      <div className="terminal-panel__header">
        <div className="terminal-panel__tabs">
          {["TERMINAL", "OUTPUT", "DEBUG CONSOLE", "PROBLEMS"].map((t) => (
            <button
              key={t}
              className={`terminal-tab${activeTab === t ? " terminal-tab--active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="terminal-panel__actions">
          <button className="terminal-action" title="New Terminal">+</button>
          <button className="terminal-action" title="Split Terminal">⊞</button>
          <button className="terminal-action" title="Kill Terminal">🗑</button>
          <button className="terminal-action" onClick={onClose} title="Close Panel">✕</button>
        </div>
      </div>

      {/* Terminal body */}
      <div className="terminal-body" onClick={() => inputRef.current?.focus()}>
        {lines.map((line, i) => (
          <div key={i} className={`t-line ${line.cls || ""}`}>
            {line.text || "\u00A0"}
          </div>
        ))}

        {/* Input line */}
        {ready && (
          <div className="t-line t-input-row">
            <span className="t-prompt">tommy@portfolio:~/portfolio $ </span>
            <input
              ref={inputRef}
              className="t-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              autoFocus
              spellCheck={false}
              aria-label="Terminal input"
            />
            <span className="t-cursor" />
          </div>
        )}

        {!ready && (
          <div className="t-line t-dim">
            <span className="t-spinner">⠋</span> Starting…
          </div>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
}

export default TerminalPanel;
